export const THEME_VARIANTS = {
  default: "Default",
  blue: "Blue",
  green: "Green",
  purple: "Purple",
  orange: "Orange",
  rose: "Rose",
} as const;

export type ThemeVariant = keyof typeof THEME_VARIANTS;

export function isThemeMode(value: string | null) {
  return value === "light" || value === "dark" || value === "system";
}

export function isThemeVariant(value: string | null): value is ThemeVariant {
  return value !== null && Object.hasOwn(THEME_VARIANTS, value);
}

// Theme utilities
export const themeUtils = {
  // Get current theme colors programmatically
  getThemeColors: () => {
    if (typeof window === "undefined") return null;

    const computedStyle = getComputedStyle(document.documentElement);
    return {
      background: computedStyle.getPropertyValue("--background").trim(),
      foreground: computedStyle.getPropertyValue("--foreground").trim(),
      primary: computedStyle.getPropertyValue("--primary").trim(),
      secondary: computedStyle.getPropertyValue("--secondary").trim(),
      muted: computedStyle.getPropertyValue("--muted").trim(),
      accent: computedStyle.getPropertyValue("--accent").trim(),
      destructive: computedStyle.getPropertyValue("--destructive").trim(),
      border: computedStyle.getPropertyValue("--border").trim(),
      ring: computedStyle.getPropertyValue("--ring").trim(),
    };
  },

  // Check if reduced motion is preferred
  prefersReducedMotion: () => {
    if (typeof window === "undefined") return false;
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  },

  // Check if high contrast is preferred
  prefersHighContrast: () => {
    if (typeof window === "undefined") return false;
    return window.matchMedia("(prefers-contrast: high)").matches;
  },

  // Apply theme variant CSS variables
  applyThemeVariant: (variant: ThemeVariant) => {
    if (typeof window === "undefined") return;

    const root = document.documentElement;
    const variantColors = getThemeVariantColors(variant);

    Object.entries(variantColors).forEach(([property, value]) => {
      root.style.setProperty(`--${property}`, value);
    });
  },
};

// Theme variant color mappings
function getThemeVariantColors(variant: ThemeVariant): Record<string, string> {
  const variants = {
    default: {
      primary: "192 76% 46%",
      "primary-foreground": "0 0% 100%",
      ring: "192 76% 46%",
      "interactive-primary": "192 76% 46%",
      "interactive-primary-hover": "192 73% 55%",
      "interactive-primary-active": "194 80% 37%",
      "text-link": "192 76% 46%",
      "text-link-hover": "192 73% 55%",
      "border-interactive": "192 76% 46%",
      "border-focus": "192 76% 46%",
    },
    blue: {
      primary: "213.6 93.9% 67.8%",
      "primary-foreground": "210 40% 98%",
      ring: "213.6 93.9% 67.8%",
    },
    green: {
      primary: "142.1 76.2% 36.3%",
      "primary-foreground": "355.7 100% 97.3%",
      ring: "142.1 76.2% 36.3%",
    },
    purple: {
      primary: "262.1 83.3% 57.8%",
      "primary-foreground": "210 40% 98%",
      ring: "262.1 83.3% 57.8%",
    },
    orange: {
      primary: "24.6 95% 53.1%",
      "primary-foreground": "210 40% 98%",
      ring: "24.6 95% 53.1%",
    },
    rose: {
      primary: "346.8 77.2% 49.8%",
      "primary-foreground": "355.7 100% 97.3%",
      ring: "346.8 77.2% 49.8%",
    },
  };

  return variants[variant] || variants.default;
}

// Theme variant CSS classes (to be used with CSS-in-JS or CSS modules)
export const themeVariantClasses = Object.keys(THEME_VARIANTS).reduce(
  (acc, variant) => {
    acc[variant as ThemeVariant] = `theme-${variant}`;
    return acc;
  },
  {} as Record<ThemeVariant, string>,
);
